import {
  fetchVideoList,
  fetchVideoTotalQty,
  fetchShortList,
  fetchShortTotalQty,
  fetchComicList,
  fetchComicTotalQty,
  fetchNovelList,
  fetchNovelTotalQty,
  SchemaCmsSectionShow,
} from 'api'
import { fetchRandomWithMultiPage } from 'utils/randomList'
import { VideoTypeEnum } from 'constant/common'
import { SORT_BY_RANDOM } from '../../../component/AreaBox/constant'

interface ContentRequestParam {
  pageSize?: number
  special_search?: number
  category_ids?: string
  actor_ids?: string
  tag_ids?: string
  region_ids?: string
  producer_ids?: string
  sortby?: string
  payment_type?: number
  uploader_id?: string | number
  serial?: string | number
}

// 排行榜僅有前100筆
const RANK_VIDEO_COUNT = 100
const RANK_TEMPLATE_ID = '19'

export function getContentApi(type: any): [any, any] {
  switch (type) {
    case VideoTypeEnum.LongVideo:
      return [fetchVideoList, fetchVideoTotalQty]
    case VideoTypeEnum.ShortVideo:
      return [fetchShortList, fetchShortTotalQty]
    case VideoTypeEnum.Comic:
      return [fetchComicList, fetchComicTotalQty]
    case VideoTypeEnum.Novel:
      return [fetchNovelList, fetchNovelTotalQty]
    default:
      return [fetchVideoList, fetchVideoTotalQty]
  }
}

const fetchWithMultiPage = async (fetchApi: any, params: any) => {
  const originalSize = params.pageSize
  const pageSize = Math.min(originalSize, 100)

  const data: any[] = []
  const requests: Promise<any[]>[] = []

  for (let i = 0; i < originalSize; i += pageSize) {
    requests.push(
      (async () => {
        const limit = Math.min(originalSize - i, pageSize)
        const partial = (
          await fetchApi({
            ...params,
            pageSize: pageSize,
            current: i / pageSize + 1,
          })
        ).data
        return partial.slice(0, limit)
      })()
    )
  }
  const results = await Promise.all(requests)
  for (const partial of results) {
    data.push(...partial)
  }

  return { data: data }
}

export async function loadData({ type, info, random, signal }: { type: any; info: any; random: any; signal: any }) {
  const requestParams = getRequestParam(info) as ContentRequestParam
  if (requestParams.pageSize == null) {
    return []
  }
  const data = {
    signal,
    pageSize: requestParams.pageSize,
    ...requestParams,
  }

  const [fetchListApi, fetchListTotalQtyApi] = getContentApi(type)

  if (!random) {
    const response = await fetchWithMultiPage(fetchListApi, data)
    return response?.data
  } else {
    /** 如果後端回傳 video_sortby 為 random, 則由前端實作 random 邏輯 */
    const randomData = await fetchRandomWithMultiPage(fetchListTotalQtyApi, fetchListApi, { ...data })
    return randomData
  }
}

// 取得排行榜資料
export async function loadGroupData({ infoList, signal }: { infoList: any[]; signal: any }) {
  const listData: Record<number, any> = {}
  const promiseList: Promise<any>[] = []
  for (let i = 0; i < infoList.length; i++) {
    const info = infoList[i]
    const [fetchListApi] = getContentApi(info.type)
    const requestParams = getRequestParam(info, 100) as ContentRequestParam
    if (requestParams.pageSize == null) {
      continue
    }
    // 排行榜在CMS固定抓取100筆，然後根據設置的數量顯示指定的筆數
    const data = {
      signal,
      ...requestParams,
      pageSize: RANK_VIDEO_COUNT,
    }
    // 要顯示的筆數
    const displayCount = Math.min(info.displayno, RANK_VIDEO_COUNT)
    const fetchPromise = fetchWithMultiPage(fetchListApi, data).then((response) => {
      listData[i] = response?.data.slice(0, displayCount)
    })
    promiseList.push(fetchPromise)
  }
  return Promise.all(promiseList).then(() => {
    return listData
  })
}

export function getRequestParam(areaInfo: SchemaCmsSectionShow, maxCount?: number) {
  if (!areaInfo) {
    console.log('area is empty')
    return {}
  }
  const category_ids = areaInfo.video_category?.join(',')
  const actor_ids = areaInfo.video_actor?.join(',')
  const tag_ids = areaInfo.video_tag?.join(',')
  const video_regions = areaInfo.video_region_ids?.join(',')
  const video_producers = Array.isArray(areaInfo.video_producer_ids) ? areaInfo.video_producer_ids.join(',') : ''
  const countLimit = maxCount ?? 0
  const size =
    countLimit > 0 ? (areaInfo.displayno <= countLimit ? areaInfo.displayno : countLimit) : areaInfo.displayno
  const { type, serial } = areaInfo

  return {
    pageSize: size,
    special_search: areaInfo.template_id === RANK_TEMPLATE_ID ? 1 : undefined, //排行榜
    category_ids: category_ids,
    actor_ids: actor_ids,
    tag_ids: tag_ids,
    region_ids: video_regions,
    producer_ids: video_producers,
    sortby: areaInfo.video_sortby === SORT_BY_RANDOM ? 'updated_at' : areaInfo.video_sortby,
    payment_type: areaInfo.video_payment_type,
    ...(areaInfo.video_sortby === 'days_hot'
      ? { hot_days: areaInfo.hot_days, hot_days_start: areaInfo.hot_days_start }
      : {}),
    ...(areaInfo.video_sortby === 'month_hot' ? { hot_month: areaInfo.hot_month } : {}),
    ...(areaInfo.video_sortby === 'year_hot' ? { hot_year: areaInfo.hot_year } : {}),
    ...(type === VideoTypeEnum.ShortVideo
      ? {
          uploader_id: areaInfo.video_uploader_id,
        }
      : {}),
    ...(type === VideoTypeEnum.Comic
      ? {
          serial: serial,
        }
      : {}),
    ...(type === VideoTypeEnum.Novel
      ? {
          serial: serial,
        }
      : {}),
  }
}
